import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Message, useChat } from "@ai-sdk/react";
import { ArrowDown, MessageSquare, Send } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from 'react-markdown';

const MemoizedMessageItem = React.memo(function MessageItem({ message }: { message: Message }) {
  return (
    <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
      <div
        className={`p-3 rounded-lg max-w-[70%] ${message.role === "user"
          ? "bg-brand text-white"
          : "bg-muted"
          }`}
      >
        <ReactMarkdown>{message.content}</ReactMarkdown>
      </div>
    </div>
  );
});

export function FloatingChatBot() {
  const { messages, input, handleSubmit, handleInputChange, status } = useChat({
    api: "https://rnrnbdqtileefwodurwk.supabase.co/functions/v1/ai-api",
  });

  const loading = status !== "ready";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isManuallyScrolling, setIsManuallyScrolling] = useState(false);
  const programmaticScrollRef = useRef(false);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'user') {
      setIsManuallyScrolling(false);
    }
  }, [messages]);

  useEffect(() => {
    const scrollableNode = scrollRef.current;
    if (!scrollableNode) return;

    const handleScroll = () => {
      if (programmaticScrollRef.current) {
        programmaticScrollRef.current = false;
        const { scrollTop, scrollHeight, clientHeight } = scrollableNode;
        if (scrollHeight - scrollTop <= clientHeight + 1) {
          if (isManuallyScrolling) {
            setIsManuallyScrolling(false);
          }
        }
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = scrollableNode;
      const atBottom = scrollHeight - scrollTop <= clientHeight + 1;

      if (atBottom) {
        if (isManuallyScrolling) {
          setIsManuallyScrolling(false);
        }
      } else {
        if (!isManuallyScrolling) {
          setIsManuallyScrolling(true);
        }
      }
    };

    scrollableNode.addEventListener('scroll', handleScroll);
    return () => {
      scrollableNode.removeEventListener('scroll', handleScroll);
    };
  }, [isManuallyScrolling]);

  useEffect(() => {
    if (!isManuallyScrolling && scrollRef.current && (messages.length > 0 || status === 'streaming')) {
      programmaticScrollRef.current = true;
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, status, isManuallyScrolling]);

  return (
    <Dialog>
      <DialogTrigger className="hidden sm:block fixed bottom-6 right-6 z-50 rounded-full p-4 h-16 w-16 bg-brand text-white shadow-lg hover:bg-brand-dark transition-all duration-300">
        <MessageSquare className="h-7 w-7" />
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] p-0 flex flex-col h-[70vh]">
        <DialogHeader className="p-6 border-b">
          <DialogTitle>Chat with Captain</DialogTitle>
          <DialogDescription>Ask me anything!</DialogDescription>
        </DialogHeader>
        <div className="flex-1 relative min-h-0">
          <div className="space-y-4 overflow-auto h-full px-4 py-4" ref={scrollRef}>
            {messages.map((m: Message) => (
              <MemoizedMessageItem key={m.id} message={m} />
            ))}
          </div>
          {isManuallyScrolling && scrollRef.current && (scrollRef.current.scrollHeight - scrollRef.current.scrollTop > scrollRef.current.clientHeight + 5) && (
            <Button
              onClick={() => {
                if (scrollRef.current) {
                  programmaticScrollRef.current = true;
                  scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                }
                setIsManuallyScrolling(false);
              }}
              variant="outline"
              size="sm"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-background/90 backdrop-blur-sm hover:bg-secondary shadow-md"
            >
              Scroll to bottom
              <ArrowDown className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
        <DialogFooter className="p-4 border-t">
          <form onSubmit={handleSubmit} className="flex w-full items-center space-x-2">
            <Input
              value={input}
              onChange={handleInputChange}
              placeholder="Type a message..."
              className="flex-1"
              disabled={loading}
            />
            <Button type="submit" size="icon" disabled={loading || !input.trim()}>
              <Send className="h-4 w-4" />
              <span className="sr-only">Send</span>
            </Button>
          </form>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
